import {
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import {
  UserIntegrationsContext,
  UserIntegrationsProvider,
} from "contexts/UserIntegrations";
import { useContext, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { SubscriptionType } from "schemas/dashboard";

import StyledPage from "components/Styled/Page";
import { PAGE_VISITED, getPageName } from "constants/trackingProps";
import Subscription from "features/Home/UpgradeDialog/Subscription";
import { trackEvent } from "utils/tracking";

import AddOnCustomEmail from "./AddOnsCustomEmail";
import CustomEmailBenefits from "./AddOnsCustomEmail/CustomEmailBenefits";
import AddOnPartnershipData from "./AddOnsPartnershipData";
import AffiliateProgram from "./AffiliateProgram";
import EmailTracking from "./EmailTracking";
import GoogleConnect from "./GoogleConnect";
import Referral from "./Referral";
import SubscriptionButton from "./SubscriptionButton";
import UserNotificationsSettings from "./UserNotifications";
import styles from "./styles";

export default function Settings() {
  const { setAlert } = useContext(AlertContext);
  const { userIntegration } = useContext(UserIntegrationsContext);
  const { currentUser } = useContext(OrganizationUserContext);
  const theme = useTheme();
  const { discoverTab } = useContext(DiscoverViewContext);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isLargeScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const isMobileScreen =
    isMediumScreen || (isLargeScreen && discoverTab !== null);
  const [searchParams] = useSearchParams();
  const { setCustomDomainDialog, subscription } =
    useContext(SubscriptionContext);

  const customDomainExample = () => {
    return `ugcby${currentUser?.name.split(" ")[0].toLowerCase()}.com`;
  };

  const customEmailExample = () => {
    return `hello@${customDomainExample()}`;
  };

  const scrollToSubscription = searchParams.get("section") === "subscription";
  const scrollToAddOn = searchParams.get("section") === "addon";
  const openEmailDialog = searchParams.get("custom-email") === "true";

  const subscriptionRef = useRef<HTMLDivElement>(null);
  const addOnRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    trackEvent(PAGE_VISITED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollToSubscription && subscriptionRef.current) {
      subscriptionRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [scrollToSubscription]);

  useEffect(() => {
    if ((openEmailDialog || scrollToAddOn) && addOnRef.current) {
      addOnRef.current.scrollIntoView({ behavior: "smooth" });
    }
    if (openEmailDialog) {
      setCustomDomainDialog(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToAddOn, openEmailDialog]);

  return (
    <StyledPage
      content={
        <Grid sx={{ pb: 3 }}>
          {!isMobileScreen && (
            <Typography variant="h3" gutterBottom sx={styles.header}>
              <strong>Settings</strong>
            </Typography>
          )}

          <Referral />

          <Divider sx={{ my: 4 }} />

          <Box
            ref={subscriptionRef}
            sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
          >
            <Typography variant="h4">Subscription</Typography>{" "}
            {!isSmallScreen && <SubscriptionButton />}
          </Box>
          {isSmallScreen && (
            <Box sx={{ ml: -1, mb: 2 }}>
              <SubscriptionButton />
            </Box>
          )}

          <Subscription setAlert={setAlert} />

          <Typography ref={addOnRef} variant="h4" gutterBottom>
            Add-Ons
          </Typography>

          <Grid container spacing={1}>
            <Grid item xs={12} md={6} lg={6} xl={4}>
              <AddOnPartnershipData />
            </Grid>
            <Grid item xs={12} md={6} lg={6} xl={4}>
              <AddOnCustomEmail />
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          <AffiliateProgram />

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" gutterBottom>
            Integrations
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs>
              <Typography variant="h6" gutterBottom>
                Google
              </Typography>
              {userIntegration?.email ? (
                <Alert severity="success" sx={{ maxWidth: "600px", my: 1 }}>
                  Your Google account is currently connected. Emails will be
                  sent from <strong>{userIntegration.email}.</strong>
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ maxWidth: "600px", my: 1 }}>
                  No Google account is currently connected.
                </Alert>
              )}

              <Typography variant="body2">
                Connect your Gmail to Bento
              </Typography>
              <Typography variant="body2">
                You can revoke your access at any time.{" "}
                <Link
                  href="https://support.google.com/accounts/answer/3466521?hl=en"
                  target="_blank"
                >
                  Manage your Google access.
                </Link>
              </Typography>
            </Grid>
            <Grid item xs="auto">
              <UserIntegrationsProvider>
                <GoogleConnect />
              </UserIntegrationsProvider>
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          {(!subscription?.customEmailData ||
            !(
              subscription.customEmailData.status === SubscriptionType.ACTIVE ||
              subscription.customEmailData.status === SubscriptionType.PAST_DUE
            )) && (
            <>
              <Typography variant="h4" gutterBottom>
                Set up a Professional Email
              </Typography>

              <Typography variant="h6" gutterBottom>
                Using a Custom Domain
              </Typography>
              <Typography paragraph>
                Get a professional Gmail inbox with your own domain (i.e.{" "}
                {customEmailExample()}). Why does this matter?
              </Typography>
              <CustomEmailBenefits />

              <Button
                sx={{ mt: 2 }}
                variant="contained"
                onClick={() => {
                  trackEvent("Set up a Professional Email Button Clicked", {
                    Source: getPageName(),
                  });
                  setCustomDomainDialog(true);
                }}
                disableElevation
              >
                Get Started
              </Button>

              <Divider sx={{ my: 4 }} />
            </>
          )}

          <Typography variant="h4" gutterBottom>
            Notifications
          </Typography>

          <Grid item xs={12}>
            <UserNotificationsSettings />
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" gutterBottom>
            Email Tracking
          </Typography>
          <Grid item xs={12}>
            <EmailTracking />
          </Grid>

          <Box sx={{ height: 50 }} />
        </Grid>
      }
    />
  );
}
